/**
 * Resource Meshes
 * Generates simple 3D meshes for all 10 collectible resource types.
 * Each mesh is kept under 100 triangles for performance.
 */

import * as THREE from 'three';

// ---------------------------------------------------------------------------
// 1. Coconut
// ---------------------------------------------------------------------------
function createCoconutMesh() {
  const geometry = new THREE.SphereGeometry(0.45, 8, 6);
  // Slightly flatten on the Y axis
  geometry.scale(1, 0.85, 1);

  const material = new THREE.MeshStandardMaterial({
    color: 0x8b4513,
    roughness: 0.9,
    metalness: 0.0,
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  return mesh;
}

// ---------------------------------------------------------------------------
// 2. Coconut Shell
// ---------------------------------------------------------------------------
function createCoconutShellMesh() {
  const group = new THREE.Group();

  // Outer half-sphere
  const outerGeometry = new THREE.SphereGeometry(
    0.35, 8, 6, 0, Math.PI * 2, 0, Math.PI * 0.5
  );
  const outerMaterial = new THREE.MeshStandardMaterial({
    color: 0x8b4513,
    roughness: 0.9,
    metalness: 0.0,
  });
  const outer = new THREE.Mesh(outerGeometry, outerMaterial);
  outer.castShadow = false;
  outer.receiveShadow = false;
  group.add(outer);

  // Inner disc to show lighter inside
  const innerGeometry = new THREE.CircleGeometry(0.35, 8);
  const innerMaterial = new THREE.MeshStandardMaterial({
    color: 0xd2b48c,
    roughness: 0.8,
    metalness: 0.0,
    side: THREE.DoubleSide,
  });
  const inner = new THREE.Mesh(innerGeometry, innerMaterial);
  inner.rotation.x = -Math.PI * 0.5;
  inner.castShadow = false;
  inner.receiveShadow = false;
  group.add(inner);

  return group;
}

// ---------------------------------------------------------------------------
// 3. Gem
// ---------------------------------------------------------------------------
function createGemMesh() {
  const geometry = new THREE.OctahedronGeometry(0.35, 0);
  const material = new THREE.MeshStandardMaterial({
    color: 0x9966ff,
    transparent: true,
    opacity: 0.8,
    emissive: 0x4400ff,
    emissiveIntensity: 0.3,
    roughness: 0.1,
    metalness: 0.2,
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  return mesh;
}

// ---------------------------------------------------------------------------
// 4. Gold Coin
// ---------------------------------------------------------------------------
function createGoldCoinMesh() {
  const geometry = new THREE.CylinderGeometry(0.3, 0.3, 0.06, 16);
  const material = new THREE.MeshStandardMaterial({
    color: 0xffd700,
    metalness: 0.8,
    roughness: 0.2,
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  return mesh;
}

// ---------------------------------------------------------------------------
// 5. Seashell
// ---------------------------------------------------------------------------
function createSeashellMesh() {
  const group = new THREE.Group();

  // Flattened torus as a simple spiral shell shape
  const torusGeometry = new THREE.TorusGeometry(0.25, 0.1, 6, 10);
  const torusMaterial = new THREE.MeshStandardMaterial({
    color: 0xfff0e0,
    roughness: 0.6,
    metalness: 0.1,
  });
  const torus = new THREE.Mesh(torusGeometry, torusMaterial);
  torus.scale.set(1, 1, 0.5);
  torus.castShadow = false;
  torus.receiveShadow = false;
  group.add(torus);

  // Small cone tip to suggest spiral
  const tipGeometry = new THREE.ConeGeometry(0.1, 0.2, 6);
  const tipMaterial = new THREE.MeshStandardMaterial({
    color: 0xffc0cb,
    roughness: 0.6,
    metalness: 0.1,
  });
  const tip = new THREE.Mesh(tipGeometry, tipMaterial);
  tip.position.set(0.25, 0, 0);
  tip.rotation.z = -Math.PI * 0.5;
  tip.castShadow = false;
  tip.receiveShadow = false;
  group.add(tip);

  return group;
}

// ---------------------------------------------------------------------------
// 6. Glass Shard
// ---------------------------------------------------------------------------
function createGlassShardMesh() {
  const vertices = new Float32Array([
    0.0, 0.4, 0.0,    // top
    -0.2, 0.0, 0.03,  // bottom-left
    0.2, 0.0, -0.03,  // bottom-right
  ]);

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
  geometry.computeVertexNormals();

  const material = new THREE.MeshStandardMaterial({
    color: 0x44ff88,
    transparent: true,
    opacity: 0.6,
    side: THREE.DoubleSide,
    roughness: 0.1,
    metalness: 0.1,
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  return mesh;
}

// ---------------------------------------------------------------------------
// 7. Driftwood
// ---------------------------------------------------------------------------
function createDriftwoodMesh() {
  const group = new THREE.Group();

  // Main log — thicker and longer so it's visible on the beach
  const logGeometry = new THREE.CylinderGeometry(0.12, 0.08, 1.4, 6);
  logGeometry.scale(1, 1, 0.75);
  const logMaterial = new THREE.MeshStandardMaterial({
    color: 0x7a5c3a,    // darker brown to contrast against sand
    roughness: 0.95,
    metalness: 0.0,
  });
  const log = new THREE.Mesh(logGeometry, logMaterial);
  log.rotation.z = Math.PI * 0.5;
  log.castShadow = false;
  log.receiveShadow = false;
  group.add(log);

  // Small branch stub for visual interest
  const branchGeometry = new THREE.CylinderGeometry(0.04, 0.03, 0.4, 5);
  const branchMaterial = new THREE.MeshStandardMaterial({
    color: 0x8b6843,
    roughness: 0.95,
    metalness: 0.0,
  });
  const branch = new THREE.Mesh(branchGeometry, branchMaterial);
  branch.position.set(0.2, 0.12, 0);
  branch.rotation.z = Math.PI * 0.3;
  branch.castShadow = false;
  branch.receiveShadow = false;
  group.add(branch);

  return group;
}

// ---------------------------------------------------------------------------
// 8. Fish
// ---------------------------------------------------------------------------
function createFishMesh() {
  const group = new THREE.Group();

  // Body: elongated sphere
  const bodyGeometry = new THREE.SphereGeometry(0.25, 8, 6);
  bodyGeometry.scale(1.8, 1, 0.8);
  const bodyMaterial = new THREE.MeshStandardMaterial({
    color: 0xc0c0c0,
    metalness: 0.3,
    roughness: 0.4,
  });
  const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
  body.castShadow = false;
  body.receiveShadow = false;
  group.add(body);

  // Tail: small triangle
  const tailVertices = new Float32Array([
    -0.45, 0.0, 0.0,   // base center
    -0.70, 0.20, 0.0,   // top
    -0.70, -0.20, 0.0,  // bottom
  ]);
  const tailGeometry = new THREE.BufferGeometry();
  tailGeometry.setAttribute('position', new THREE.BufferAttribute(tailVertices, 3));
  tailGeometry.computeVertexNormals();

  const tailMaterial = new THREE.MeshStandardMaterial({
    color: 0xc0c0c0,
    metalness: 0.3,
    roughness: 0.4,
    side: THREE.DoubleSide,
  });
  const tail = new THREE.Mesh(tailGeometry, tailMaterial);
  tail.castShadow = false;
  tail.receiveShadow = false;
  group.add(tail);

  return group;
}

// ---------------------------------------------------------------------------
// 9. Melody Fragment
// ---------------------------------------------------------------------------
function createMelodyFragmentMesh() {
  const geometry = new THREE.SphereGeometry(0.25, 8, 6);
  const material = new THREE.MeshStandardMaterial({
    color: 0xffff00,
    emissive: 0xffaa00,
    emissiveIntensity: 0.5,
    roughness: 0.3,
    metalness: 0.1,
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  return mesh;
}

// ---------------------------------------------------------------------------
// 10. Sand Dollar
// ---------------------------------------------------------------------------
function createSandDollarMesh() {
  const group = new THREE.Group();

  // Flat disc
  const discGeometry = new THREE.CylinderGeometry(0.35, 0.35, 0.04, 16);
  const discMaterial = new THREE.MeshStandardMaterial({
    color: 0xf5deb3,
    roughness: 0.8,
    metalness: 0.0,
  });
  const disc = new THREE.Mesh(discGeometry, discMaterial);
  disc.castShadow = false;
  disc.receiveShadow = false;
  group.add(disc);

  // Star pattern — five tiny radial cylinders on top
  const armMaterial = new THREE.MeshStandardMaterial({
    color: 0xfffde8,
    roughness: 0.7,
    metalness: 0.0,
  });
  for (let i = 0; i < 5; i++) {
    const armGeometry = new THREE.CylinderGeometry(0.02, 0.02, 0.22, 4);
    const arm = new THREE.Mesh(armGeometry, armMaterial);
    // Rotate arm to lie flat and radiate outward
    arm.rotation.x = Math.PI * 0.5;
    const angle = (i / 5) * Math.PI * 2;
    arm.position.set(
      Math.cos(angle) * 0.11,
      0.03,
      Math.sin(angle) * 0.11
    );
    arm.rotation.y = -angle;
    arm.castShadow = false;
    arm.receiveShadow = false;
    group.add(arm);
  }

  return group;
}

// ---------------------------------------------------------------------------
// Master factory
// ---------------------------------------------------------------------------
/**
 * Create a resource mesh by type name.
 * @param {string} resourceType — one of the 10 resource type keys
 * @returns {THREE.Object3D} — a Group or Mesh ready to add to the scene
 */
export function createResourceMesh(resourceType) {
  const creators = {
    coconut: createCoconutMesh,
    coconut_shell: createCoconutShellMesh,
    gem: createGemMesh,
    gold_coin: createGoldCoinMesh,
    seashell: createSeashellMesh,
    glass_shard: createGlassShardMesh,
    driftwood: createDriftwoodMesh,
    fish: createFishMesh,
    melody_fragment: createMelodyFragmentMesh,
    sand_dollar: createSandDollarMesh,
  };

  const creator = creators[resourceType];
  if (!creator) {
    console.warn(`[Resources] Unknown mesh type: ${resourceType}`);
    return new THREE.Mesh(
      new THREE.SphereGeometry(0.2),
      new THREE.MeshStandardMaterial({ color: 0xff0000 })
    );
  }

  const mesh = creator();
  mesh.userData.resourceType = resourceType;
  return mesh;
}

// Export individual creators for direct use / customization
export {
  createCoconutMesh,
  createCoconutShellMesh,
  createGemMesh,
  createGoldCoinMesh,
  createSeashellMesh,
  createGlassShardMesh,
  createDriftwoodMesh,
  createFishMesh,
  createMelodyFragmentMesh,
  createSandDollarMesh,
};
